import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MediaQuery, Chip } from '@spotahome/ui-library';
import { trans } from '@spotahome/soyuz/client';

import Metrics from '@spotahome/soyuz-tracking';
import classNames from 'classnames';

import Bell from '@spotahome/marketplace-common/src/components/icons/Bell';

import { useUserInfoContext } from '@spotahome/marketplace-common/src/context/UserInfoContext';

import AlertButton from '@spotahome/marketplace-common/src/components/AlertButton';
import { getActiveFiltersCount } from '@spotahome/marketplace-common/src/utils/filters';

import BILLS from '../Filters/bills-config';

import AvailabilitiesFilter from './components/AvailabilitiesFilter';
import PropertyTypeFilter from './components/PropertyTypeFilter';
import BudgetFilter from './components/BudgetFilter';

import './FiltersExposed.scss';

const BILLS_NAMES = BILLS.map(({ name }) => name);

const handleSaveMySearch = () => {
  Metrics.ga.sendEvent('Search', 'Alert Primary CTA', 'click button');
};

const FiltersExposed = ({
  filters,
  cityId,
  currencyIsoCode,
  totalResults,
  onFiltersToggle,
  onFiltersUpdate,
  onFiltersClose,
  maxPriceInCity,
  minimumStaying,
  sortingAlgorithm
}) => {
  const { isUserLogged } = useUserInfoContext();
  const { FromLaptop } = MediaQuery;

  const {
    'move-in': moveIn,
    'move-out': moveOut,
    moveOutFrom,
    moveOutTo,
    moveInFrom,
    moveInTo,
    'rentalType[]': rentalTypes,
    'type[]': propertyTypes
  } = filters;

  const [filtersCount, setFiltersCount] = useState(0);

  useEffect(() => {
    const filtersWithoutExposedFilters = {
      ...filters,
      'move-in': undefined,
      'move-out': undefined,
      moveOutFrom: undefined,
      moveOutTo: undefined,
      moveInFrom: undefined,
      moveInTo: undefined,
      budget: undefined,
      'rentalType[]': undefined,
      'type[]': undefined,
      'topology[]': undefined,
      'features[]': filters['features[]'].filter(
        feature => !BILLS_NAMES.includes(feature)
      )
    };

    setFiltersCount(getActiveFiltersCount(filtersWithoutExposedFilters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const [openFilter, setOpenFilter] = useState(null);

  const selectedBills = BILLS_NAMES.filter(billName =>
    filters['features[]'].includes(billName)
  );

  const handleCloseExposedFilter = () => {
    setOpenFilter(null);
  };

  const toggleExposedFilter = filterName => () => {
    const isFilterOpen = openFilter === filterName;
    const eventLabel = isFilterOpen
      ? `close-${filterName}`
      : `open-${filterName}`;

    Metrics.ga.sendEvent('Search', 'exposed-filters', eventLabel);

    if (isFilterOpen) {
      handleCloseExposedFilter();
    } else {
      setOpenFilter(filterName);
    }
    onFiltersClose();
  };

  const handleFiltersToggle = () => {
    Metrics.ga.sendEvent('Search', 'exposed-filters', 'open-filters');
    setOpenFilter(null);
    onFiltersToggle();
  };

  const filterButtonClassNames = 'filters-exposed__button';
  const filterChipClassNames = classNames(
    'filters-exposed__button',
    'filters-exposed__button--with-count',
    'filters-exposed__button--bigger',
    {
      'filters-exposed__chip--with-filters-selected': !!filtersCount
    }
  );

  const alertButtonClasses = classNames(
    'filters-exposed__button',
    'filters-exposed__save-search-button--bigger',
    'filters-exposed__save-search-button'
  );

  return (
    <div className="filters-exposed">
      <AvailabilitiesFilter
        className={filterButtonClassNames}
        moveIn={moveIn}
        moveOut={moveOut}
        selectedRentalTypes={rentalTypes}
        isFilterOpen={openFilter === 'date'}
        onToggleFilter={toggleExposedFilter('date')}
        onFiltersUpdate={onFiltersUpdate}
        minimumStaying={minimumStaying}
        totalResults={totalResults}
        moveOutFrom={moveOutFrom}
        moveOutTo={moveOutTo}
        moveInFrom={moveInFrom}
        moveInTo={moveInTo}
      />

      <PropertyTypeFilter
        className={filterButtonClassNames}
        selectedPropertyTypes={propertyTypes}
        isFilterOpen={openFilter === 'property-type'}
        onToggleFilter={toggleExposedFilter('property-type')}
        onFiltersUpdate={onFiltersUpdate}
        totalResults={totalResults}
        filters={filters}
      />

      <BudgetFilter
        className={filterButtonClassNames}
        isFilterOpen={openFilter === 'price'}
        selectedBills={selectedBills}
        onToggleFilter={toggleExposedFilter('price')}
        currencyIsoCode={currencyIsoCode}
        sortingAlgorithm={sortingAlgorithm}
        onFiltersUpdate={onFiltersUpdate}
        totalResults={totalResults}
        filters={filters}
        maxPriceInCity={maxPriceInCity}
        cityId={cityId}
      />
      <Chip.Filter
        label={trans('search.filter_header.more_filters')}
        className={filterChipClassNames}
        onClick={handleFiltersToggle}
        dataTest="more-filters-button"
        isSelected={!!filtersCount}
        renderTrailingIcon={() =>
          filtersCount ? <span>{filtersCount}</span> : null
        }
      />
      <FromLaptop>
        <AlertButton
          buttonClassName={alertButtonClasses}
          buttonProps={{
            size: 'small',
            color: 'primary',
            renderIcon: () => <Bell />
          }}
          cityId={cityId}
          filters={filters}
          isUserLogged={isUserLogged}
          onClick={handleSaveMySearch}
          dataTest="alert-button-exposed-filters"
        />
      </FromLaptop>
    </div>
  );
};

FiltersExposed.propTypes = {
  totalResults: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    flexibleMoveIn: PropTypes.number,
    flexibleMoveOut: PropTypes.number,
    budget: PropTypes.string,
    'move-in': PropTypes.string,
    'move-out': PropTypes.string,
    'rentalType[]': PropTypes.arrayOf(PropTypes.string),
    'type[]': PropTypes.arrayOf(PropTypes.string),
    'features[]': PropTypes.arrayOf(PropTypes.string),
    moveInFrom: PropTypes.string,
    moveInTo: PropTypes.string,
    moveOutFrom: PropTypes.string,
    moveOutTo: PropTypes.string
  }),
  cityId: PropTypes.string.isRequired,
  currencyIsoCode: PropTypes.string.isRequired,
  onFiltersUpdate: PropTypes.func.isRequired,
  onFiltersClose: PropTypes.func.isRequired,
  onFiltersToggle: PropTypes.func.isRequired,
  maxPriceInCity: PropTypes.number.isRequired,
  minimumStaying: PropTypes.number.isRequired,
  sortingAlgorithm: PropTypes.string.isRequired
};

FiltersExposed.defaultProps = {
  filters: {}
};

export default FiltersExposed;
